import React, { useCallback } from 'react';

import { ClassName } from '../../../../../types';

import { IconsEnum } from '../../../../../assets/icons/types';

import { TaskNanoID, TaskUUID } from '../../../tasksTypes';

import {
  CONTINUE_TASK_QUERY,
  ContinueTaskQueryResponse
} from '../../../queries/continueTask.query';

import { useContinueTask } from '../../../hooks/useContinueTask';

import { ConfirmModalButton } from '../../../../../helpers/buttons/ConfirmModalButton';

import { ProjectsTaskCache } from '../../../../projectsTasks/ProjectsTaskCache';
import { SmartContractCache } from '../../../../smartContracts/SmartContractCache';
import { TaskCache } from '../../../TaskCache';
import { TaskProcessingJobCache } from '../../../../taskProcessingJobs/TaskProcessingJobCache';

import { stringsKeys, tasksKeys } from '../../../../../locales/keys';

interface ContinueTaskModalButtonProps {
  taskUuid: TaskUUID;
  taskNanoId: TaskNanoID;
  className?: ClassName;
  icon?: IconsEnum;
  iconClassName?: ClassName;
}

function ContinueTaskModalButton({
  taskUuid,
  taskNanoId,
  className,
  icon = IconsEnum.REPLY_OUTLINE,
  iconClassName
}: ContinueTaskModalButtonProps) {
  const { continueTask, continueTaskErrorMessage, continueTaskLoading } =
    useContinueTask<ContinueTaskQueryResponse>({
      query: CONTINUE_TASK_QUERY,
      cacheKeys: [
        ProjectsTaskCache.showProjectTreeCacheKey(),
        SmartContractCache.selectByTaskFieldCacheKey(taskNanoId),
        SmartContractCache.showCacheKey(),
        TaskCache.showCacheKey(),
        TaskCache.showDashboardCacheKey(),
        TaskCache.showHeaderActionsCacheKey(),
        TaskCache.showSidebarCacheKey(),
        TaskProcessingJobCache.taskTaskProcessingJobCacheKey(taskNanoId),
        TaskCache.showResultsCacheKey()
      ]
    });

  const handleContinueTaskSubmit = useCallback(
    () => continueTask({ uuid: taskUuid }),
    [continueTask, taskUuid]
  );

  return (
    <ConfirmModalButton
      className={
        className ||
        'flex text-left dark:hover:bg-gray-800 hover:bg-gray-100 px-4 py-2 text-sm w-full whitespace-nowrap'
      }
      disabled={continueTaskLoading}
      errorMessage={continueTaskErrorMessage}
      i18nButtonText={tasksKeys.buttons.continue}
      i18nSubmitText={tasksKeys.buttons.continue}
      i18nText={stringsKeys.areYouSureYouWantToContinueTheTask}
      i18nTitle={tasksKeys.buttons.continue}
      icon={icon}
      iconClassName={iconClassName}
      isLoading={continueTaskLoading}
      modalIcon={IconsEnum.EXCLAMATION}
      modalIconClassName="h-6 w-6 text-red-600"
      onSubmit={handleContinueTaskSubmit}
    />
  );
}

export default ContinueTaskModalButton;
